/* eslint-disable @next/next/no-img-element */
import styles from './LandMap.module.scss'
import React, { useEffect, useRef, useState } from 'react'
import classnames, { Value } from 'classnames'
import coordinates from './map-coordinates.json'
import { MapEngine, ActiveTile } from './MapEngine'
import { getNFTs } from '../../utils/getNFTs'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import Button from '../Button/Button'
import Modal from '../Modal/Modal'
interface Props {
    className?: Value
}

const auctionableTiles = [
    [111, 23],
    [141, 38],
    [86, 45],
    [151, 45],
    [61, 51],
    [111, 15],
    [95, 25],
    [105, 26],
    [121, 34],
    [82, 38],
    [64, 45],
    [71, 45],
    [109, 47],
    [121, 48],
    [131, 48],
    [144, 48],
    [163, 49],
    [120, 56],
    [126, 56],
    [96, 58],
]

const LandMap: React.FC<Props> = ({ className }) => {
    const canvasRef = useRef<HTMLDivElement>(null)
    const miniMapRef = useRef<HTMLDivElement>(null)
    const [mapEngineRef, setMapEngineRef] = useState<MapEngine>()
    const [activeTile, setActiveTile] = useState<ActiveTile>()
    const { connection } = useConnection()
    const { publicKey } = useWallet()
    const [search, setSearch] = useState('')

    const renderCustomizeModal = () => {
        return (
            <div>
                <h4>Customize tile</h4>
                <div>
                    <label>
                        <div>Twitter handle</div>
                        <input type="text" placeholder="@solice_io" />
                    </label>
                </div>
                <div>
                    <input type="text" />
                </div>
                <div>
                    <Button>Save</Button>
                </div>
            </div>
        )
    }

    const renderActiveTile = (activeTile: ActiveTile) => {
        return (
            <Modal modalContent={renderCustomizeModal}>
                {(open) => (
                    <div className={styles.tileOverlay}>
                        <div className={styles.close} onClick={handleClose}>
                            X
                        </div>

                        {!activeTile.owned && activeTile.auctionable && (
                            <div className={styles.info}>
                                <div className={styles.description}>This tile will be auctioned</div>
                            </div>
                        )}

                        {activeTile.src && (
                            <div className={styles.image}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={activeTile.src} alt={activeTile.ownerName} />
                            </div>
                        )}
                        {(activeTile.ownerName || activeTile.twitterHandle) && (
                            <div>
                                {activeTile.ownerName && <div className={styles.owner}>{activeTile.ownerName}</div>}
                                {activeTile.twitterHandle && (
                                    <div className={styles.handle}>
                                        <a href={activeTile.twitterLink} target="_blank" rel="noreferrer">
                                            {activeTile.twitterHandle}
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={styles.info}>
                            <div className={styles.title}>Location</div>
                            <div className={styles.description}>
                                [{activeTile.x}, {activeTile.y}]
                            </div>
                        </div>
                        {activeTile.size && (
                            <div className={styles.info}>
                                <div className={styles.title}>Size</div>
                                <div className={styles.description}>
                                    {activeTile.size}X{activeTile.size}{' '}
                                    <span className={styles.smallDescription}>
                                        ({activeTile.size * activeTile.size}{' '}
                                        {activeTile.size === 1 ? 'Parcel' : 'Parcels'})
                                    </span>
                                </div>
                            </div>
                        )}
                        {activeTile.owned && (
                            <div className={styles.info}>
                                <div className={styles.title}>You own this tile</div>
                            </div>
                        )}
                        {!activeTile.owned && activeTile.auctionable && (
                            <div className={styles.info}>
                                <Button href={'https://market.solice.io?tab=auction'} className={styles.button}>
                                    Go to auctions
                                </Button>
                            </div>
                        )}
                        {/* <Button className={styles.button} onClick={open}>
                            Customize Tile
                        </Button> */}
                    </div>
                )}
            </Modal>
        )
    }

    useEffect(() => {
        // set the map hightlight from search.
        if (!mapEngineRef) return
        const searchMatches = search.match(/(\d+),\s?(\d+)/i)
        if (searchMatches && searchMatches[1] && searchMatches[2]) {
            mapEngineRef.setActiveTile(parseInt(searchMatches[1], 10), parseInt(searchMatches[2], 10))
        }
    }, [mapEngineRef, search])

    useEffect(() => {
        let mapEngine: MapEngine
            // eslint-disable-next-line @typescript-eslint/no-extra-semi
        ;(async () => {
            if (!canvasRef.current || !miniMapRef.current) return

            mapEngine = new MapEngine(canvasRef.current, miniMapRef.current)

            await mapEngine.start(
                coordinates.map((coordinate) => {
                    const isAuctionable = !!auctionableTiles.find(([x, y]) => {
                        return coordinate.x === x && coordinate.y === y
                    })

                    return {
                        ...coordinate,
                        auctionable: isAuctionable,
                    }
                })
            )

            mapEngine.onActiveTile((tile) => {
                setActiveTile(tile)
            })

            setMapEngineRef(mapEngine)
        })()

        return () => mapEngine.stop()
    }, [])

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-extra-semi
        ;(async () => {
            if (!mapEngineRef) return

            if (!connection || !publicKey) {
                mapEngineRef.renderOwnedTiles([])
            }

            if (connection && publicKey) {
                const nfts = await getNFTs(connection, publicKey)
                const lands = nfts.filter(({ symbol }) => symbol === 'SLCLAND')

                const formattedLands: [number, number][] = lands
                    .filter((land) => {
                        // Filter out non lands without coordinates as a safety
                        const matches = land.name.match(/\[(\d+),?\s(\d+)\]/)
                        return matches && matches[1] && matches[2]
                    })
                    .map((land) => {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        const matches = land.name.match(/\[(\d+),?\s(\d+)\]/)!
                        const x = parseInt(matches[1])
                        const y = parseInt(matches[2])
                        return [x, y]
                    })

                mapEngineRef.renderOwnedTiles(formattedLands)
            }
        })()
    }, [connection, mapEngineRef, publicKey])

    const handleZoomIn = () => {
        if (!mapEngineRef) return

        mapEngineRef.zoomIn()
    }

    const handleZoomOut = () => {
        if (!mapEngineRef) return

        mapEngineRef.zoomOut()
    }

    const handleZoomReset = () => {
        if (!mapEngineRef) return

        mapEngineRef.resetZoom()
    }

    const handleClose = () => {
        if (!mapEngineRef) return

        mapEngineRef.clearActiveTile()
    }

    return (
        <>
            <div key="canvas" className={classnames(styles.container, className)}>
                <div ref={miniMapRef} className={classnames(styles.miniMapCanvas)} />
                <div ref={canvasRef} className={styles.canvas} />
                <input
                    type="text"
                    className={styles.searchBar}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    placeholder={'Search land: 138, 78'}
                ></input>
                <div className={styles.controls}>
                    <button className={styles.controlButton} onClick={handleZoomIn}>
                        <img src="/plus.png" alt="" />
                    </button>
                    <button className={styles.controlButton} onClick={handleZoomOut}>
                        <img src="/minus.png" alt="" />
                    </button>
                    <button className={styles.controlButton} onClick={handleZoomReset}>
                        <img src="/reset.png" alt="" />
                    </button>
                </div>
                {activeTile && renderActiveTile(activeTile)}
            </div>
        </>
    )
}

export default LandMap
